import { createApi, fakeBaseQuery, SkipToken } from "@reduxjs/toolkit/dist/query/react";
import { collection, doc, getDoc, getDocs, query, setDoc, where } from "firebase/firestore";
import { db } from "@/firebase";
import { User } from "@/types";
import { ApiError } from "@/types/api";
import { addUser } from "@/store/usersSlice";
import { pick } from "lodash";
import { setUser } from "@/features/auth/slice";

export const usersApi = createApi({
  reducerPath: "usersApi",
  baseQuery: fakeBaseQuery<ApiError>(),
  tagTypes: ["userOrganizations"],
  endpoints: (builder) => ({
    fetchProfile: builder.query<User, string | SkipToken>({
      queryFn: async (userId, { dispatch }) => {
        const docRef = doc(db, `users/${String(userId)}`);
        const snapshot = await getDoc(docRef);

        if (snapshot.exists()) {
          const profile = { ...snapshot.data(), uid: userId };
          dispatch(addUser(profile));

          return {
            data: profile,
          };
        }

        return {
          error: {
            status: 404,
            statusText: "Usuario no encontrado",
          },
        };
      },
    }),
    createProfile: builder.mutation<User, Partial<User> | SkipToken>({
      queryFn: async (user, { dispatch }) => {
        if (!user?.uid) {
          return {
            error: {
              status: 400,
              statusText: "Petición inválida",
            },
          };
        }

        const payload = {
          ...user,
          createdBy: user.uid,
          createdAt: Date.now(),
        };

        try {
          const collectionRef = doc(db, `users/${user?.uid}`);
          await setDoc(collectionRef, payload);
          dispatch(addUser(payload));

          return {
            data: user.uid,
          };
        } catch (error) {
          return {
            error: {
              status: 500,
              statusText: "Error al crear el perfil",
            },
          };
        }

      },
    }),
  updateUser: builder.mutation<
    any,
    { draft: Partial<User> & { uid: string } }
  >({
    queryFn: async ({ draft }, { getState, dispatch }) => {
      try {
        const store = (getState as () => any)();

        if (!draft?.uid) {
          return {
            error: {
              status: 400,
              message: "Consulta Inválida",
            },
          };
        }

        const userRef = doc(db, `users/${draft.uid}`);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          return {
            error: {
              status: 404,
              message: "El usuario no existe",
            },
          };
        }

        const ALLOWED_FIELDS = [
          "name",
          "sanitizedName",
          "sanitizedLastname",
          "lastname",
          "phone",
          "locations"
        ];
        

        const payload: Partial<User> = {
          ...pick(draft, ALLOWED_FIELDS),
          sanitizedName: draft.name?.toLowerCase(),
          sanitizedLastname: draft.lastname?.toLowerCase(),
          lastUpdatedBy: store?.auth?.user?.uid,
          lastUpdatedAt: Date.now(),
        };

        await setDoc(userRef, payload, { merge: true });

        const updatedUser = {
          ...userSnap.data(),
          ...payload,
          uid: draft.uid,
        };

        dispatch(setUser(updatedUser)); 

        return {
          data: updatedUser,
        };
      } catch (error) {
        console.error(error);
        return {
          error: {
            status: 500,
            message: "Error al actualizar el usuario",
          },
        };
      }
    },

    invalidatesTags: ["userOrganizations"],
  }),
  fetchUserByEmail: builder.query<User | null, string>({
    async queryFn(email) {
      const q = query(
        collection(db, "users"),
        where("email", "==", email)
      );

      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return { data: { ...doc.data(), uid: doc.id } as User };
      }

      return { data: null };
    },
  }),


  }),
});

export const { useFetchProfileQuery, useLazyFetchProfileQuery, useCreateProfileMutation, useUpdateUserMutation, useFetchUserByEmailQuery, useLazyFetchUserByEmailQuery } = usersApi as any;
